import { Injectable } from '@angular/core';
import { LOCAL_STORAGE_KEY, SESSION_STORAGE_KEY } from '@core/configs';
import { Mode } from '@core/constants/enum.common';
import { LocalStorageService, SessionStorageService } from '../common';

export const LOG_TYPE = {
  API: 'API',
  FIREBASE: 'Firebase',
  OTHER: 'Other'
};

export const LOG_STATUS = {
  START: 'START',
  OK: 200,
  UNDEFINED: 'UNDEFINED',
};

export const LOG_RESULT = {
  OK: 'OK',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR'
};

export const LOG_CONTENT = {
  RETRY: 'Retry',
  RETRY_FAIL: 'Retry failed',
  SYNC_SUCCESS: 'Sync success',
  AUTO_SYNC_DETECTED: 'Auto sync detected',
  HAS_CHANGE: 'Has changed',
  NO_CHANGE: 'No change',
};

export interface CareMoniDiagnosticLog {
  logType: string;
  subject: string;
  status: string | number;
  message: any;
  // Pass the same logId on the START and END entries of one call so they can be matched up.
  logId?: string;
}

interface NativeLogSavedResult {
  success: boolean;
  eventId?: string;
  error?: string;
}

interface AndroidDiagnosticLogBridge {
  saveDiagnosticLog(logLine: string): void;
}

declare global {
  interface Window {
    AndroidBridge?: AndroidDiagnosticLogBridge;
    onNativeLogSaved?: (result: NativeLogSavedResult) => void;
  }
}

/**
 * Sends individual diagnostic logs (as a single formatted text line) to the Native App.
 *
 * Native is responsible for Room persistence, batching, network waiting,
 * retries, and sending the logs to the backend logging API.
 */
@Injectable({ providedIn: 'root' })
export class NativeDiagnosticLogService {
  // Fetched once from DeviceLicenses.logEnabled (0 = disabled, 1 = enabled).
  private logEnabled = 0;

  constructor(
    private sessionStorageService: SessionStorageService,
    private localStorageService: LocalStorageService,
  ) {
    window.onNativeLogSaved = (result: NativeLogSavedResult): void => {
      if (result.success) {
        console.debug('Log saved locally:', result.eventId);
      } else {
        console.error('Cannot save log:', result.error);
      }
    };
  }

  setLogEnabled(enabled: number): void {
    this.logEnabled = enabled === 1 ? 1 : 0;
  }

  sendCareMoniLog(params: CareMoniDiagnosticLog): boolean {
    if (!this.isTvMode() || this.logEnabled !== 1) {
      return false;
    }

    if (!window.AndroidBridge?.saveDiagnosticLog) {
      console.warn('AndroidBridge is unavailable');
      return false;
    }

    const logLine = this.formatLogLine(params);
    // Leave eventId empty so Native can generate it.
    window.AndroidBridge?.saveDiagnosticLog(logLine);
    return true;
  }

  private isTvMode(): boolean {
    return this.localStorageService.getItem(LOCAL_STORAGE_KEY.mode) == Mode.TV;
  }

  // Generate once at START and pass the same value into the matching END call, so both
  // entries can be matched up by id. Kept short (5 chars) as requested.
  generateLogId(): string {
    return Math.random().toString(36).slice(2, 7);
  }

  // Format: YYYY/MM/DD hh:mm:ss - {stafflogin} - {logType} - {logId}|{subject} - {status} - {message}
  private formatLogLine(params: CareMoniDiagnosticLog): string {
    const staffInfo = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.staffInfo);
    const staffLogin = this.formatWithNullFallback(staffInfo?.stfCd);
    const datetime = this.formatDatetime(new Date());
    const logId = params.logId ?? this.generateLogId();
    const logType = this.formatWithNullFallback(params.logType);
    const subject = this.formatWithNullFallback(params.subject);
    const status = this.formatWithNullFallback(params.status);
    const message = this.formatWithNullFallback(this.parseToString(params.message));

    return `${datetime} - ${staffLogin} - ${logType} - ${logId}|${subject} - ${status} - ${message}`;
  }

  // Falls back to 'NULL' for null/undefined/empty-string, but keeps a numeric 0 (e.g. status 0) as-is.
  private formatWithNullFallback(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return 'NULL';
    }
    return String(value);
  }

  private formatDatetime(date: Date): string {
    return `${date.getFullYear()}/${this.padTwoDigits(date.getMonth() + 1)}/${this.padTwoDigits(date.getDate())} ${this.padTwoDigits(date.getHours())}:${this.padTwoDigits(date.getMinutes())}:${this.padTwoDigits(date.getSeconds())}`;
  }

  // Adds a leading zero when the number is a single digit, e.g. 5 -> "05", 12 -> "12"
  private padTwoDigits(n: number): string {
    return String(n).padStart(2, '0');
  }

  private parseToString(inp: any) {
    if (inp === null || inp === undefined) {
      return '';
    }

    let result: string;
    if (typeof inp === 'object') {
      try {
        result = JSON.stringify(inp);
      } catch (error) {
        console.error('Error stringifying object:', error);
        return '';
      }
    } else {
      result = String(inp);
    }

    // Keep the whole log entry on a single line — collapse any real line breaks/tabs.
    return result.replace(/[\r\n\t]+/g, ' ').trim();
  }
}
